import { SkeletonProps } from 'antd'
import isNil from 'lodash-es/isNil'
import isUndefined from 'lodash-es/isUndefined'
import { FunctionComponent, ReactNode } from 'react'
import styled from 'styled-components'
import { Duration } from '@/components/common/duration-select'
import KolDetailDataBlock from '@/components/kol/common/kol-detail-data-block'
import KolDetailStatistic from '@/components/kol/common/kol-detail-statistic'
import AudienceGenderStatistic from '@/components/kol/common/statistics/audience-gender-statistic'
import AudienceMainAgeStatistic from '@/components/kol/common/statistics/audience-main-age-statistic'
import EngagementRateStatistic from '@/components/kol/common/statistics/engagement-rate-statistic'
import FansUpRateStatistic from '@/components/kol/common/statistics/fans-up-rate-statistic'
import FollowerStatistic from '@/components/kol/common/statistics/follower-statistic'
import KScore from '@/components/kol/common/statistics/k-score'
import SponsoredCoverageStatistic from '@/components/kol/common/statistics/sponsored-coverage-statistic'
import ViewRateStatistic from '@/components/kol/common/statistics/view-rate-statistic'
import { statisticPlatformSupport } from '@/constants/kol-detail/statistic-platform-support'
import {
  DURATION_MONTH_INTEGER_MAP,
  KolDetailDuration,
} from '@/hooks/use-authorization/routes'
import { useIntl } from '@/i18n/hooks/use-intl'
import type { Nullable } from '@/types/nullable'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { Platform, PlatformShortcode } from '@/utils/convert-platform'

interface BasicInfoProps {
  kolInfo?: KolSchemaModel
  platform: Platform
  isKolInfoLocked: boolean
  isFetchingKolInfo: boolean
  showMockData?: boolean
  duration: KolDetailDuration
}

export const statisticSkeletonProps: SkeletonProps = {
  active: true,
  title: { width: 60 },
  paragraph: { rows: 1, width: 100 },
}

const Statistics: FunctionComponent<BasicInfoProps> = ({
  kolInfo,
  platform,
  isKolInfoLocked,
  isFetchingKolInfo,
  showMockData,
  duration,
}) => {
  const { formatMessage, formatNumber } = useIntl()

  const displayCountStat = [
    PlatformShortcode.TikTok,
    PlatformShortcode.Twitter,
  ].includes(platform.shortcode)

  const kpi = kolInfo?.kpi.find((kpi) => kpi.platform === platform.shortcode)

  const statisticLoading: SkeletonProps | boolean = isFetchingKolInfo
    ? statisticSkeletonProps
    : false

  const { interactive, avgShortPlayCount, avgPvCount, postCount } = ((): {
    interactive: Nullable<number> | undefined
    avgShortPlayCount: number | null
    avgPvCount: number | null
    postCount: number | null
  } => {
    if (isUndefined(kpi)) {
      return {
        interactive: undefined,
        avgShortPlayCount: null,
        avgPvCount: null,
        postCount: null,
      }
    }

    if (duration === Duration.ThreeMonths) {
      return {
        interactive: kpi.interactive3m,
        avgShortPlayCount: kpi.avgShortPlayCount3m,
        avgPvCount: kpi.avgPv3m,
        postCount: kpi.postCount3m,
      }
    }

    return {
      interactive: kpi.interactive6m,
      avgShortPlayCount: kpi.avgShortPlayCount6m,
      avgPvCount: kpi.avgPv6m,
      postCount: kpi.postCount6m,
    }
  })()

  return (
    <KolDetailDataBlock
      colSpan={16}
      header={{
        title: `${platform.title} ${formatMessage({ id: 'competitive_brand_analyses:overview' })}`,
      }}
      kolId={kolInfo?.id}
      kolMetrics='Key Metrics Summary'
      kolName={kolInfo?.name}
      platform={platform.shortcode}
    >
      <KScore
        duration={duration}
        isKolInfoLocked={isKolInfoLocked}
        kolInfo={kolInfo}
        loading={isFetchingKolInfo}
        platformShortcode={platform.shortcode}
        showMockData={showMockData}
      />
      <StatisticsWrapper>
        <FollowerStatistic
          followerCount={kpi?.follower}
          loading={statisticLoading}
        />
        <EngagementRateStatistic
          duration={duration}
          isKolInfoLocked={isKolInfoLocked}
          kpi={kpi}
          loading={statisticLoading}
          showMockData={showMockData}
        />
        {statisticPlatformSupport.viewRate.includes(platform.shortcode) && (
          <ViewRateStatistic
            duration={duration}
            isKolInfoLocked={isKolInfoLocked}
            kpi={kpi}
            loading={statisticLoading}
            platformShortCode={platform.shortcode}
            showMockData={showMockData}
          />
        )}
        <FansUpRateStatistic
          duration={duration}
          isKolInfoLocked={isKolInfoLocked}
          kpi={kpi}
          loading={statisticLoading}
          showMockData={showMockData}
        />
        {statisticPlatformSupport.audience.includes(platform.shortcode) && (
          <>
            <AudienceGenderStatistic
              isKolInfoLocked={isKolInfoLocked}
              kpi={kpi}
              showMockData={showMockData}
              statisticLoading={statisticLoading}
            />
            <AudienceMainAgeStatistic
              isFetchingKolInfo={isFetchingKolInfo}
              isKolInfoLocked={isKolInfoLocked}
              kolInfo={kolInfo}
              platformCode={platform.code}
              showMockData={showMockData}
              statisticLoading={statisticLoading}
            />
          </>
        )}
        {statisticPlatformSupport.sponsoredCoverage.includes(
          platform.shortcode,
        ) && (
          <SponsoredCoverageStatistic
            duration={duration}
            isKolInfoLocked={isKolInfoLocked}
            kpi={kpi}
            showMockData={showMockData}
            statisticLoading={statisticLoading}
          />
        )}
        {displayCountStat && (
          <>
            <KolDetailStatistic
              loading={statisticLoading}
              title={formatMessage({ id: 'kol:post_card_content' })}
              tooltip={{
                title: formatMessage({ id: 'kol:kolcard_content_tooltip' }),
              }}
              value={postCount}
            />
            <KolDetailStatistic
              loading={statisticLoading}
              title={formatMessage({
                id: 'kol:data_from_kol_avg_eng',
              })}
              tooltip={{
                title: formatMessage(
                  { id: 'kol:platform_data_avg_post_eng_tooltip' },
                  { n: DURATION_MONTH_INTEGER_MAP[duration] },
                ),
              }}
              value={
                isNil(interactive) || interactive === 0
                  ? null
                  : formatNumber(Math.round(interactive))
              }
            />{' '}
            <KolDetailStatistic
              loading={statisticLoading}
              title={formatMessage({ id: 'kol:card_avg_view' })}
              tooltip={{
                title: formatMessage(
                  {
                    id: 'kol:platform_data_avg_video_view_tooltip',
                  },
                  { n: DURATION_MONTH_INTEGER_MAP[duration] },
                ),
              }}
              value={((): ReactNode => {
                const avgView =
                  PlatformShortcode.TikTok === platform.shortcode
                    ? avgShortPlayCount
                    : avgPvCount
                return isNil(avgView) || avgView === 0
                  ? null
                  : formatNumber(Math.round(avgView))
              })()}
            />
          </>
        )}
      </StatisticsWrapper>
    </KolDetailDataBlock>
  )
}

export default Statistics

const StatisticsWrapper = styled.div`
  display: grid;
  gap: 40px 20px;
  grid-template-columns: repeat(4, 1fr);
`
